/**
 * A module's declaration of itself. It is deliberately framework-neutral — no
 * NestJS, no React, no lucide types — because both apps read the same object:
 * the API turns `permissions` into rows and guard checks, the panel turns
 * `navigation` and `resources` into a sidebar and Refine resources.
 */

/** Refine's action vocabulary, which the permission keys reuse verbatim. */
export type CrudAction = 'list' | 'show' | 'create' | 'edit' | 'delete'

export interface PermissionDescriptor {
  /**
   * Namespace for the permission. The `core` module owns the unprefixed names
   * (`users`, `roles`, …); every other module MUST prefix with its own key —
   * `hr.employees`, `bi.reports` — so two modules can never claim one resource.
   */
  resource: string
  action: CrudAction | (string & {})
  /** Shown in the permission matrix. Written for the person granting it. */
  description: string
}

export interface ModuleNavItem {
  label: string
  /** Absolute path inside the panel. Omit when `children` carries the links. */
  href?: string
  /** A lucide-react icon name, resolved by the panel at render time. */
  icon?: string
  iconClassName?: string
  /** Hidden unless the signed-in user holds this permission key. */
  permission?: string
  children?: readonly ModuleNavItem[]
  /** Keeps the entry out of the command palette without hiding the sidebar item. */
  paletteHidden?: boolean
}

export interface ModuleNavGroup {
  label: string
  items: readonly ModuleNavItem[]
}

/** One Refine resource. `name` MUST equal the permission `resource` it is gated by. */
export interface ModuleResource {
  /**
   * Refine-ийн resource нэр. МӨН эрхийн нэрийн зай (`hr.employees:list`) тул
   * цэгтэй байдаг.
   */
  name: string
  label: string

  /**
   * API-гийн зам, эхний `/`-гүйгээр — `hr/employees`.
   *
   * Өгөгдөөгүй бол `name`-ийн цэгийг зураас болгож гаргана (`resourceApiPath`).
   * Зөвхөн зам нь нэрнээсээ ӨӨР үед бичнэ.
   */
  apiPath?: string
  list?: string
  show?: string
  create?: string
  edit?: string
  canDelete?: boolean
}

export interface ModuleManifest {
  /** Stable machine key. Used as the DB primary key and the permission prefix. */
  key: string
  name: string
  description: string
  /** The module's own version, independent of the platform's. */
  version: string
  /** A lucide-react icon name. */
  icon: string
  /**
   * A core module underpins the platform: it cannot be disabled or removed,
   * because the screens that would re-enable it live inside it.
   */
  isCore?: boolean

  /**
   * Whether a FRESH install switches this module on. Defaults to `true`.
   *
   * Set `false` for a module that ships as a reference rather than a feature:
   * its code stays in the repo to copy from, but nothing appears in the
   * sidebar and its API routes stay shut until someone enables it.
   *
   * ! This is the INSTALL default only. Once a module row exists, the toggle
   * ! in Settings wins — re-seeding never flips a module back.
   */
  enabledByDefault?: boolean
  permissions: readonly PermissionDescriptor[]
  navigation: readonly ModuleNavGroup[]
  resources: readonly ModuleResource[]
}

/**
 * Resource-ийн API зам.
 *
 * ! Refine-ийн resource нэр нь ЦЭГТЭЙ (`hr.employees`) — эрхийн нэрийн зайтай
 * ! нийцүүлсэн. API зам нь ЗУРААСТАЙ (`hr/employees`). Энэ хоёрыг хооронд нь
 * ! хөрвүүлэхгүй бол data provider `/api/hr.employees` гэсэн байхгүй зам рүү
 * ! очно — HR бүхэлдээ `useCustom`-оор гараар бичигдсэн шалтгаан яг энэ байв.
 */
export const resourceApiPath = (resource: ModuleResource): string =>
  resource.apiPath ?? resource.name.replace(/\./g, '/')

/** Бүх модулийн resource нэр → API зам. Data provider үүнийг хэрэглэнэ. */
export const buildResourcePathIndex = (manifests: readonly ModuleManifest[]): Map<string, string> =>
  new Map(
    manifests.flatMap((manifest) =>
      manifest.resources.map((resource) => [resource.name, resourceApiPath(resource)] as const)
    )
  )

/** `<resource>:<action>` — the exact string the API guards and `can()` compare. */
export const permissionKey = (permission: Pick<PermissionDescriptor, 'resource' | 'action'>): string =>
  `${permission.resource}:${permission.action}`

/** Every permission key a module declares. */
export const modulePermissionKeys = (module: ModuleManifest): string[] =>
  module.permissions.map(permissionKey)

/**
 * Fails loudly on the two mistakes that only surface much later: a module
 * claiming a resource namespace that is not its own, and two modules declaring
 * the same permission. Run at boot on both sides.
 */
export const assertManifestsAreConsistent = (manifests: readonly ModuleManifest[]): void => {
  const seenKeys = new Map<string, string>()
  const seenModules = new Set<string>()

  for (const manifest of manifests) {
    if (seenModules.has(manifest.key)) {
      throw new Error(`Duplicate module key: ${manifest.key}`)
    }

    seenModules.add(manifest.key)

    for (const permission of manifest.permissions) {
      const key = permissionKey(permission)
      const owner = seenKeys.get(key)

      if (owner) {
        throw new Error(`Permission "${key}" is declared by both "${owner}" and "${manifest.key}"`)
      }

      // The core module owns the flat namespace; everyone else prefixes.
      if (!manifest.isCore && !permission.resource.startsWith(`${manifest.key}.`)) {
        throw new Error(
          `Module "${manifest.key}" declares "${key}", but a non-core module must prefix its ` +
            `resources with "${manifest.key}." (e.g. "${manifest.key}.${permission.resource}:${permission.action}")`,
        )
      }

      seenKeys.set(key, manifest.key)
    }
  }
}

/** permissionKey → owning module key. The API uses it to gate disabled modules. */
export const buildPermissionOwnerIndex = (manifests: readonly ModuleManifest[]): Map<string, string> =>
  new Map(
    manifests.flatMap(manifest => modulePermissionKeys(manifest).map(key => [key, manifest.key] as const)),
  )

/**
 * Turns a manifest's permission tuple into the exact union of its keys — mapping
 * over the tuple rather than crossing resources with actions, so `dashboard:show`
 * is a valid key and `dashboard:delete` is not. Requires the manifest be declared
 * `as const`, which is what makes the tuple's element types survive.
 */
export type PermissionKeysOf<T extends readonly { resource: string; action: string }[]> = {
  [K in keyof T]: `${T[K]['resource']}:${T[K]['action']}`
}[number]
